import { API_URL } from '../utils/api';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Plus, Trash2, Wallet, DollarSign, Euro, TrendingUp,
  Calendar, CheckCircle, Clock, AlertCircle, CreditCard, Pencil
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { cn } from '../lib/utils';


const CURRENCY_CONFIG = {
  TRY: { symbol: '₺', label: 'TL', icon: Wallet, color: 'text-green-600', bg: 'bg-green-50' },
  USD: { symbol: '$', label: 'USD', icon: DollarSign, color: 'text-blue-600', bg: 'bg-blue-50' },
  EUR: { symbol: '€', label: 'EUR', icon: Euro, color: 'text-purple-600', bg: 'bg-purple-50' }
};

const STATUS_CONFIG = {
  pending: { label: 'Bekliyor', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  partial: { label: 'Kısmi Ödeme', color: 'bg-blue-100 text-blue-800', icon: TrendingUp },
  paid: { label: 'Ödendi', color: 'bg-green-100 text-green-800', icon: CheckCircle }
};

const Receivables = () => {
  const { user, token } = useAuth();
  const [receivables, setReceivables] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedReceivable, setSelectedReceivable] = useState(null);
  const [editingReceivable, setEditingReceivable] = useState(null);
  const [filterCurrency, setFilterCurrency] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const [formData, setFormData] = useState({
    customer_name: '',
    amount: '',
    currency: 'TRY',
    due_date: '',
    description: ''
  });

  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    note: ''
  });

  const headers = { Authorization: `Bearer ${token}` };
  const canManage = user?.permissions?.includes('all') || user?.permissions?.includes('accounting_manage');
  const canView = user?.permissions?.includes('all') || user?.permissions?.includes('accounting_view') || canManage;

  const fetchData = async () => {
    try {
      setLoading(true);
      const [receivablesRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/receivables`, { headers }),
        axios.get(`${API_URL}/api/receivables/summary`, { headers })
      ]);
      setReceivables(receivablesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Veri yükleme hatası:', error);
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) {
      fetchData();
    }
  }, [canView]);

  const resetForm = () => {
    setFormData({
      customer_name: '',
      amount: '',
      currency: 'TRY',
      due_date: '',
      description: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_name.trim() || !formData.amount) {
      toast.error('Müşteri adı ve tutar zorunludur');
      return;
    }

    try {
      if (editingReceivable) {
        // Düzenleme
        await axios.put(`${API_URL}/api/receivables/${editingReceivable.id}`, {
          ...formData,
          amount: parseFloat(formData.amount)
        }, { headers });
        toast.success('Alacak güncellendi');
      } else {
        // Yeni ekleme
        await axios.post(`${API_URL}/api/receivables`, {
          ...formData,
          amount: parseFloat(formData.amount)
        }, { headers });
        toast.success('Alacak eklendi');
      }
      setIsModalOpen(false);
      setEditingReceivable(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Hata oluştu');
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!paymentData.amount || !selectedReceivable) {
      toast.error('Tutar zorunludur');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/receivables/${selectedReceivable.id}/payment`,
        {
          ...paymentData,
          amount: parseFloat(paymentData.amount)
        },
        { headers }
      );
      toast.success('Tahsilat eklendi');
      setIsPaymentModalOpen(false);
      setPaymentData({ amount: '', payment_date: new Date().toISOString().split('T')[0], note: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Hata oluştu');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu alacağı silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API_URL}/api/receivables/${id}`, { headers });
      toast.success('Alacak silindi');
      fetchData();
    } catch (error) {
      toast.error('Silinemedi');
    }
  };

  const openPaymentModal = (receivable) => {
    setSelectedReceivable(receivable);
    setPaymentData({
      amount: (receivable.amount - receivable.paid_amount).toFixed(2),
      payment_date: new Date().toISOString().split('T')[0],
      note: ''
    });
    setIsPaymentModalOpen(true);
  };

  const openEditModal = (receivable) => {
    setEditingReceivable(receivable);
    setFormData({
      customer_name: receivable.customer_name || '',
      amount: receivable.amount?.toString() || '',
      currency: receivable.currency || 'TRY',
      due_date: receivable.due_date ? receivable.due_date.split('T')[0] : '',
      description: receivable.description || ''
    });
    setIsModalOpen(true);
  };

  const openNewModal = () => {
    setEditingReceivable(null);
    resetForm();
    setIsModalOpen(true);
  };

  const formatCurrency = (amount, currency) => {
    const config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.TRY;
    return `${config.symbol}${amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR');
  };

  // Filtreleme
  const filteredReceivables = receivables.filter(r => {
    if (filterCurrency !== 'all' && r.currency !== filterCurrency) return false;
    if (filterStatus !== 'all' && r.status !== filterStatus) return false;
    return true;
  });

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Bu sayfayı görüntüleme yetkiniz yok.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="receivables-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CreditCard className="h-6 w-6" />
            Alacaklar
          </h1>
          <p className="text-muted-foreground">Manuel alacak takibi</p>
        </div>
        {canManage && (
          <Button onClick={openNewModal} data-testid="add-receivable-btn">
            <Plus className="h-4 w-4 mr-2" />
            Yeni Alacak
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(CURRENCY_CONFIG).map(([currency, config]) => {
            const data = summary[currency] || { total: 0, paid: 0, remaining: 0, count: 0 };
            const Icon = config.icon;
            
            return (
              <Card key={currency} className={cn("border-l-4", config.color.replace('text-', 'border-'))}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={cn("p-2 rounded-lg", config.bg)}>
                        <Icon className={cn("h-5 w-5", config.color)} />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">{config.label} Alacak</p>
                        <p className={cn("text-xl font-bold", config.color)}>
                          {config.symbol}{data.remaining.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>
                    <div className="text-right text-sm">
                      <p className="text-muted-foreground">{data.count} kayıt</p>
                      {data.paid > 0 && (
                        <p className="text-green-600">
                          {config.symbol}{data.paid.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} tahsil
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={filterCurrency} onValueChange={setFilterCurrency}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Para Birimi" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tümü</SelectItem>
            <SelectItem value="TRY">TL</SelectItem>
            <SelectItem value="USD">USD</SelectItem>
            <SelectItem value="EUR">EUR</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Durum" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tümü</SelectItem>
            <SelectItem value="pending">Bekliyor</SelectItem>
            <SelectItem value="partial">Kısmi Ödeme</SelectItem>
            <SelectItem value="paid">Ödendi</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Müşteri</TableHead>
                <TableHead>Tutar</TableHead>
                <TableHead>Ödenen</TableHead>
                <TableHead>Kalan</TableHead>
                <TableHead>Vade</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead className="text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredReceivables.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Alacak kaydı bulunmuyor
                  </TableCell>
                </TableRow>
              ) : (
                filteredReceivables.map(r => {
                  const remaining = r.amount - (r.paid_amount || 0);
                  const statusConfig = STATUS_CONFIG[r.status] || STATUS_CONFIG.pending;
                  const currConfig = CURRENCY_CONFIG[r.currency] || CURRENCY_CONFIG.TRY;
                  const isOverdue = r.due_date && new Date(r.due_date) < new Date() && r.status !== 'paid';
                  
                  return (
                    <TableRow key={r.id} className={cn(isOverdue && "bg-red-50")}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{r.customer_name}</p>
                          {r.description && (
                            <p className="text-xs text-muted-foreground">{r.description}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className={cn("font-semibold", currConfig.color)}>
                        {formatCurrency(r.amount, r.currency)}
                      </TableCell>
                      <TableCell className="text-green-600">
                        {formatCurrency(r.paid_amount || 0, r.currency)}
                      </TableCell>
                      <TableCell className={cn("font-semibold", remaining > 0 ? "text-red-600" : "text-green-600")}>
                        {formatCurrency(remaining, r.currency)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3 text-muted-foreground" />
                          <span className={cn(isOverdue && "text-red-600 font-medium")}>
                            {formatDate(r.due_date)}
                          </span>
                          {isOverdue && <AlertCircle className="h-3 w-3 text-red-500" />}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusConfig.color}>
                          {statusConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {canManage && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditModal(r)}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Pencil className="h-3 w-3 mr-1" />
                              Düzenle
                            </Button>
                          )}
                          {canManage && r.status !== 'paid' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openPaymentModal(r)}
                              className="text-green-600 hover:text-green-700"
                            >
                              <Wallet className="h-3 w-3 mr-1" />
                              Tahsilat
                            </Button>
                          )}
                          {canManage && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-500"
                              onClick={() => handleDelete(r.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Yeni/Düzenle Alacak Modal */}
      <Dialog open={isModalOpen} onOpenChange={(open) => { setIsModalOpen(open); if (!open) setEditingReceivable(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingReceivable ? 'Alacak Düzenle' : 'Yeni Alacak'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Müşteri Adı *</label>
              <Input
                placeholder="Müşteri adı..."
                value={formData.customer_name}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                autoFocus
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Tutar *</label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Para Birimi</label>
                <Select
                  value={formData.currency}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, currency: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TRY">₺ TL</SelectItem>
                    <SelectItem value="USD">$ USD</SelectItem>
                    <SelectItem value="EUR">€ EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">Vade Tarihi</label>
              <Input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">Açıklama</label>
              <Input
                placeholder="Opsiyonel açıklama..."
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setIsModalOpen(false); setEditingReceivable(null); }}>
                İptal
              </Button>
              <Button type="submit">{editingReceivable ? 'Güncelle' : 'Kaydet'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Tahsilat Modal */}
      <Dialog open={isPaymentModalOpen} onOpenChange={setIsPaymentModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Tahsilat Ekle</DialogTitle>
          </DialogHeader>
          {selectedReceivable && (
            <div className="mb-4 p-3 bg-muted rounded-lg">
              <p className="font-medium">{selectedReceivable.customer_name}</p>
              <p className="text-sm text-muted-foreground">
                Toplam: {formatCurrency(selectedReceivable.amount, selectedReceivable.currency)} | 
                Kalan: {formatCurrency(selectedReceivable.amount - (selectedReceivable.paid_amount || 0), selectedReceivable.currency)}
              </p>
            </div>
          )}
          <form onSubmit={handlePayment} className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Tahsilat Tutarı *</label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={paymentData.amount}
                onChange={(e) => setPaymentData(prev => ({ ...prev, amount: e.target.value }))}
                autoFocus
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">Ödeme Tarihi</label>
              <Input
                type="date"
                value={paymentData.payment_date}
                onChange={(e) => setPaymentData(prev => ({ ...prev, payment_date: e.target.value }))}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">Not</label>
              <Input
                placeholder="Opsiyonel not..."
                value={paymentData.note}
                onChange={(e) => setPaymentData(prev => ({ ...prev, note: e.target.value }))}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsPaymentModalOpen(false)}>
                İptal
              </Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700">
                <Wallet className="h-4 w-4 mr-2" />
                Tahsilat Kaydet
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Receivables;
